import { ICampaignConfig, ICampaignInit, ECampaignType } from 'types/campaign';
import { env } from '@byted/ecom-mobile-lib';
import { has, Map } from 'immutable';
import { fixDateStr } from 'utils/time';

// 满减信息
function processPromotionFull(item: ICampaignInit) {
    const stype = Number(item.stype);
    if (
        !item ||
        (stype !== ECampaignType.FullDiscountWithMultiShop && stype !== ECampaignType.FullDiscount)
    )
        return {};
    if (!item.full_discount || item.full_discount.length === 0) return {};

    const promotion_string = item.full_discount
        .map((fullItem) => {
            // 判断是否每满 X 减 X, 如果没有就降级判断 110 类型, 实际上应该就判断per_level, server部分接口没传 , 耦合设计.
            return `${stype === ECampaignType.FullDiscountWithMultiShop ? '跨店' : ''}${
                fullItem.per_level ? '每' : ''
            }满${fullItem.full / 100}减${fullItem.discount / 100}`;
        })
        .join(' ');

    return {
        promotionString: promotion_string
    };
}

/*
 * 后端的活动配置信息目前没有完全支持这些特性，
 * 所以前端针对stype定义了默认的config，
 * 所有配置项优先读取后端字段
 */
const defaultConfig: ICampaignConfig = {
    name: '',
    beginTime: new Date(),
    endTime: new Date(),
    preTime: new Date(),
    currentTime: new Date(),
    supportGiveUpActivity: false,
    supportStyleInGoodsList: false,
    campaignScene: {
        toutiao: true,
        toutiaoProduct: true,
        weixin: true,
        outapp: false
    },
    notifyBarStyle: 1,
    campaignBarStyle: 1,
    percent: 0,
    isPre: false,
    customChecker: () => true,
    needRefresh: false,
    needPreEndTime: false,
    promotionString: '',
    extraFunc: (item: ICampaignInit) => {
        return {};
    },
    urlParams: {},
    url: '',
    urlName: '',
    waitForSale: false,
    canUseCoupon: false
};

const campaignType = [1, 2, 3, 5, 7, 8, 9];
const promotionType = [ECampaignType.FullDiscount, ECampaignType.FullDiscountWithMultiShop];

const campaignConfig: Record<number, Partial<ICampaignConfig>> = {
    1: {
        name: '活动',
        supportStyleInGoodsList: true
    },
    3: {
        name: '秒杀',
        campaignBarStyle: 2,
        needRefresh: true
    },
    5: {
        name: '达人专享价',
        supportStyleInGoodsList: true
    },
    7: {
        name: '限时活动',
        campaignBarStyle: 2,
        supportStyleInGoodsList: true
    },
    8: {
        name: '快闪抢购活动',
        campaignBarStyle: 5,
        needPreEndTime: true,
        needRefresh: true
    },
    9: {
        name: '快闪普通活动',
        campaignBarStyle: 5
    }
};

const promotionConfig: Record<number, Partial<ICampaignConfig>> = {
    [ECampaignType.FullDiscount]: {
        // 满减不需要前缀
        name: '满减',
        descPrefix: '',
        descSuffix: '',
        extraFunc: processPromotionFull,
        urlName: '促销活动'
    },
    [ECampaignType.FullDiscountWithMultiShop]: {
        name: '跨店满减',
        descPrefix: '跨店',
        descSuffix: '可跨店使用',
        extraFunc: processPromotionFull,
        urlName: '促销活动'
    }
};

/**
 * 获取促销活动信息，促销可能有多个
 * @param multiCampaign
 * @returns {*}
 */
export const getPromotionInfo = (multiCampaign: ICampaignInit[]) => {
    if (!multiCampaign) return [];
    let res = [];
    for (let item of multiCampaign) {
        if (Object.prototype.hasOwnProperty.call(promotionConfig, item.stype)) {
            res.push(item);
        }
    }

    return res;
};

/**
 * 获取普通平台活动信息, 且活动只能存在一个
 * @param multiCampaign
 * @returns {*}
 */
export const getCampaignInfo = (multiCampaign: ICampaignInit[]) => {
    if (!multiCampaign) return null;
    let res = null;
    for (let item of multiCampaign) {
        if (Object.prototype.hasOwnProperty.call(campaignConfig, item.stype)) {
            res = item;
            break;
        }
    }

    return res;
};

/* eslint-disable complexity */
export const getCampaignConfig = (campaign: ICampaignInit | null, needStock = true) => {
    if (!campaign) return null;

    const needStockStatus = needStock ? campaign.left_stock >= 0 : needStock;

    /**
     * 检查库存是否还有剩余，没有剩余则认为该活动无效
     */
    if (needStockStatus && (!campaign.left_stock || !campaign.stock || campaign.left_stock <= 0)) {
        return null;
    }

    /**
     * 活动的时间校验
     */
    if (!campaign.begin_time || !campaign.end_time || !campaign.current_time) return null;

    /**
     * 校验stype是否符合预期
     */
    const stype = campaign.stype;
    const useConfig = promotionType.indexOf(stype) > -1 ? promotionConfig : campaignConfig;
    const config = has(useConfig, stype) && useConfig[stype];
    if (!config) return null;

    let extraFeature = {};
    if (config.extraFunc) extraFeature = config.extraFunc(campaign);

    const coupon = campaign.coupon === 1;

    const _defaultConfig = {
        ...defaultConfig,
        ...config,
        ...campaign,
        ...extraFeature,
        canUseCoupon: coupon
    };

    if (!_defaultConfig.customChecker()) return null;

    /**
     * 检查活动是否允许在当前环境中参加
     */
    // const campaignScene = _defaultConfig.campaignScene;
    // if (
    //     (env.isInToutiao && !campaignScene.toutiao) ||
    //     (env.isInToutiaoProduct && !env.isInToutiao && !campaignScene.toutiaoProduct) ||
    //     (env.isInWechat && !campaignScene.weixin) ||
    //     (!env.isInToutiaoProduct && !env.isInWechat && !campaignScene.outapp)
    // ) {
    //     return null;
    // }

    const { begin_time, end_time, current_time, pre_time } = _defaultConfig;
    const beginTime = new Date(fixDateStr(begin_time));
    const endTime = new Date(fixDateStr(end_time));
    const currentTime = new Date(fixDateStr(current_time));
    const preTime = pre_time && new Date(fixDateStr(pre_time));

    if (beginTime.getTime() >= endTime.getTime() || currentTime.getTime() >= endTime.getTime()) {
        return null;
    }
    if (_defaultConfig.support_pre && (!preTime || currentTime.getTime() < preTime.getTime())) {
        return null;
    }

    _defaultConfig.beginTime = beginTime;
    _defaultConfig.endTime = endTime;
    if (_defaultConfig.support_pre && pre_time) {
        _defaultConfig.preTime = preTime;
        _defaultConfig.isPre = currentTime.getTime() < beginTime.getTime();
        if (_defaultConfig.isPre && _defaultConfig.needPreEndTime) {
            const pre_end_time = new Date(beginTime.getTime() - 1800000);
            _defaultConfig.isPre = currentTime.getTime() < pre_end_time.getTime();
            _defaultConfig.waitForSale =
                currentTime.getTime() < beginTime.getTime() &&
                currentTime.getTime() > pre_end_time.getTime();
        }
    }
    _defaultConfig.currentTime = currentTime;
    _defaultConfig.percent = (campaign.stock - campaign.left_stock) / campaign.stock;

    return _defaultConfig;
};
